import { useMemo } from "react";

export const useOptionCalculations = ({
  strikes,
  expectedMove,
  expectedDays,
}) => {
  // Calculate best option algorithm - ROBUST VERSION
  const calculateBestOptions = useMemo(() => {
    if (!strikes.length || !expectedMove || !expectedDays) return null;

    const results = strikes
      .map((strikeData) => {
        const strike = strikeData.strike;

        const tradingDay = strikeData.trading_days.reduce((prev, curr) => {
          return Math.abs(curr.trading_days_passed - expectedDays) <
            Math.abs(prev.trading_days_passed - expectedDays)
            ? curr
            : prev;
        });

        const scenario = tradingDay.scenarios[`${expectedMove}%`];

        if (!scenario) return null;

        const availableMoves = Object.keys(tradingDay.scenarios).map((key) =>
          parseInt(key, 10)
        );
        const minMove = Math.min(...availableMoves);
        const maxMove = Math.max(...availableMoves);

        const entryPrice = tradingDay.theoretical_price_now;
        const exitPrice = scenario.theoretical_price_future;
        const priceChange = scenario.price_change;
        const returnPct = scenario.price_change_pct;
        const riskRewardRatio = priceChange / entryPrice;

        // ROBUST SCORING: Test performance across multiple scenarios
        const timeScenarios = [];
        for (let dayOffset = -2; dayOffset <= 2; dayOffset++) {
          const testDay = expectedDays + dayOffset;
          const testTradingDay = strikeData.trading_days.find(
            (td) => td.trading_days_passed === testDay
          );
          if (testTradingDay) {
            const testScenario = testTradingDay.scenarios[`${expectedMove}%`];
            if (testScenario) {
              timeScenarios.push({
                day: testDay,
                returnPct: testScenario.price_change_pct,
                profit: testScenario.price_change,
              });
            }
          }
        }

        const moveScenarios = [];
        for (let moveOffset = -2; moveOffset <= 2; moveOffset++) {
          const testMove = expectedMove + moveOffset;
          if (testMove >= minMove && testMove <= maxMove) {
            const testScenario = tradingDay.scenarios[`${testMove}%`];
            if (testScenario) {
              moveScenarios.push({
                move: testMove,
                returnPct: testScenario.price_change_pct,
                profit: testScenario.price_change,
              });
            }
          }
        }

        const allScenarioReturns = [
          ...timeScenarios.map((s) => s.returnPct),
          ...moveScenarios.map((s) => s.returnPct),
        ];

        const avgReturn = allScenarioReturns.length
          ? allScenarioReturns.reduce((a, b) => a + b, 0) /
            allScenarioReturns.length
          : returnPct;

        const minReturn = allScenarioReturns.length
          ? Math.min(...allScenarioReturns)
          : returnPct;

        const worstCaseProfit = [...timeScenarios, ...moveScenarios].reduce(
          (min, s) => (s.profit < min ? s.profit : min),
          priceChange
        );

        const profitableScenarios = allScenarioReturns.filter(
          (r) => r > 0
        ).length;
        const profitableRatio = allScenarioReturns.length
          ? profitableScenarios / allScenarioReturns.length
          : 1;

        const stdDev = allScenarioReturns.length
          ? Math.sqrt(
              allScenarioReturns
                .map((r) => Math.pow(r - avgReturn, 2))
                .reduce((a, b) => a + b, 0) / allScenarioReturns.length
            )
          : 0;

        const robustScore =
          avgReturn * 0.25 +
          minReturn * 0.2 +
          returnPct * 0.15 +
          profitableRatio * 100 * 0.2 +
          (stdDev > 0 ? -stdDev * 0.05 : 0);

        const traditionalScore =
          returnPct * 0.4 +
          riskRewardRatio * 100 * 0.2 +
          (1 / entryPrice) * 10 * 0.1;

        return {
          strike,
          iv: strikeData.iv,
          bid: strikeData.bid,
          ask: strikeData.ask,
          last: strikeData.last,
          entryPrice,
          exitPrice,
          priceChange,
          returnPct,
          riskRewardRatio,
          actualDays: tradingDay.trading_days_passed,
          daysToExpiration: tradingDay.days_to_expiration,
          tradingDay: tradingDay.trading_day,
          score: robustScore,
          traditionalScore,
          avgReturn,
          minReturn,
          worstCaseProfit,
          profitableRatio,
          scenariosTested: allScenarioReturns.length,
          profitableScenarios,
          consistency: 100 - stdDev,
        };
      })
      .filter(Boolean);

    results.sort((a, b) => b.score - a.score);

    return results;
  }, [strikes, expectedMove, expectedDays]);

  return { calculateBestOptions };
};
